import { formatDate as angularFormatDate } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';

import { isLikelyRecordId, LookupOption } from '../core/lookup.service';

export type DisplayDateMode = 'date' | 'datetime';

@Pipe({ name: 'ccDate', standalone: true })
export class FormatDatePipe implements PipeTransform {
  transform(value: unknown, mode: DisplayDateMode = 'date'): string {
    return formatDisplayDate(value, mode);
  }
}

@Pipe({ name: 'ccMoney', standalone: true })
export class FormatMoneyPipe implements PipeTransform {
  transform(value: unknown, fallback = '-'): string {
    return formatDisplayMoney(value, fallback);
  }
}

@Pipe({ name: 'ccEnum', standalone: true })
export class FormatEnumPipe implements PipeTransform {
  transform(value: unknown, fallback = '-'): string {
    return formatDisplayEnum(value, fallback);
  }
}

@Pipe({ name: 'lookupLabel', standalone: true })
export class LookupLabelPipe implements PipeTransform {
  transform(value: unknown, options: LookupOption[] | undefined | null, fallback = '-'): string {
    return lookupDisplayLabel(value, options, fallback);
  }
}

export function formatDisplayDate(value: unknown, mode: DisplayDateMode = 'date'): string {
  const date = parseDisplayDate(value);
  if (!date) {
    return '-';
  }

  const pattern = mode === 'datetime' ? 'MMM d, y, h:mm a' : 'MMM d, y';
  return angularFormatDate(date, pattern, 'en-US');
}

export function formatDisplayMoney(value: unknown, fallback = '-'): string {
  const amount = parseMoneyAmount(value);
  if (amount === null) {
    return fallback;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2
  }).format(amount);
}

export function formatDisplayEnum(value: unknown, fallback = '-'): string {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  return toTitleCase(String(value));
}

export function isEnumLikeValue(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  const text = value.trim();
  return text.length > 0 && (/[_.-]/.test(text) || /^[A-Z0-9]+$/.test(text));
}

export function lookupDisplayLabel(value: unknown, options: LookupOption[] | undefined | null, fallback = '-'): string {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (Array.isArray(value)) {
    const labels = value
      .map((entry) => lookupDisplayLabel(entry, options, fallback))
      .filter((entry) => entry !== fallback);
    return labels.length > 0 ? labels.join(', ') : fallback;
  }

  const text = String(value);
  return options?.find((option) => option.value === text)?.label ?? (isLikelyRecordId(text) ? fallback : text);
}

function parseDisplayDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const text = value.trim();
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (isoDate) {
    return dateFromParts(Number(isoDate[1]), Number(isoDate[2]), Number(isoDate[3]));
  }

  const usDate = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  if (usDate) {
    return dateFromParts(Number(usDate[3]), Number(usDate[1]), Number(usDate[2]));
  }

  const normalizedIso = text.replace(/\.(\d{3})\d+(Z|[+-]\d{2}:?\d{2})$/, '.$1$2');
  const parsed = new Date(normalizedIso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseMoneyAmount(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().replace(/[$,\s]/g, '');
  if (normalized === '') {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function dateFromParts(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toTitleCase(value: string): string {
  const normalized = value
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .toLowerCase();

  return normalized
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
